import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import InviteSheet from './InviteSheet';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #F5A623, #DD8A0A)',
  'linear-gradient(135deg, #0A8FA8, #066D81)',
  'linear-gradient(135deg, #E5503E, #C23B2A)',
  'linear-gradient(135deg, #6BAA1E, #4E8012)',
  'linear-gradient(135deg, #9B59B6, #7D3C98)',
  'linear-gradient(135deg, #3498DB, #1A6EA8)',
];

export const GROUP_SORTS = [
  { id: 'tournaments', label: 'Tournois',  key: 'tournaments' },
  { id: 'wins',        label: 'Victoires', key: 'wins' },
  { id: 'winrate',     label: 'Win rate',  key: 'winrate', parse: (v) => parseInt(v, 10) || 0 },
];

function memberInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}

// Props :
//  - g : le groupe (name, owner, inviteCode, memberDetails…)
//  - currentUserId : id de l'utilisateur courant (détermine s'il est propriétaire)
//  - manageable : affiche la roue dentée (Quitter / Renommer / Gérer les membres)
//  - onChanged() : appelé après une action mutante (le parent refetch ses groupes)
export default function GroupCard({ g, currentUserId, manageable = false, onChanged }) {
  const navigate = useNavigate();
  const [sortId, setSortId] = useState('tournaments');
  const [showInvite, setShowInvite] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(g.name);
  const [managingMembers, setManagingMembers] = useState(false);
  const [busy, setBusy] = useState(false);

  const isOwner = !!currentUserId && g.owner === currentUserId;
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const members = Array.isArray(g.memberDetails) && g.memberDetails.length > 0
    ? g.memberDetails
    : (Array.isArray(g.members) ? g.members.map((id, i) => ({ id, userId: id })) : []);

  const sortCfg = GROUP_SORTS.find(s => s.id === sortId);
  const sortedMembers = [...members].sort((a, b) => {
    const val = m => sortCfg.parse ? sortCfg.parse(m[sortCfg.key]) : (m[sortCfg.key] || 0);
    return val(b) - val(a);
  });
  const headlineColor = sortId === 'winrate' ? 'var(--text)' : sortId === 'wins' ? 'var(--lime, #6BAA1E)' : 'var(--sun-2)';

  const handleInvite = () => { setShowInvite(true); };

  const handleLeave = async () => {
    setMenuOpen(false);
    if (!window.confirm(`Quitter le groupe "${g.name}" ?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/groups/${g.id}/leave`, {}, auth);
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setBusy(false); }
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameValue.trim()) return;
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/groups/${g.id}`, { name: renameValue.trim() }, auth);
      setRenaming(false);
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setBusy(false); }
  };

  const handleExclude = async (memberId, name) => {
    if (!window.confirm(`Exclure ${name} du groupe ?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/groups/${g.id}/members/${memberId}`, auth);
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    if (!window.confirm(`Supprimer définitivement le groupe "${g.name}" ? Cette action est irréversible.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/groups/${g.id}`, auth);
      onChanged && onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="tourney-hero on-hero" style={{ height: 110, padding: 12, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflow: 'visible' }}>
        <div className="hero-photo photo-court" />
        <div className="hero-tint" />
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
          <span className="chip">{isOwner ? 'Fondateur' : 'Membre'}</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {manageable && (
              <button aria-label="Options du groupe" onClick={() => setMenuOpen(o => !o)}
                style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.95)', color: '#1a1a1a', boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" style={{ fill: '#1a1a1a' }}>
                  <circle cx="12" cy="5" r="2.3"/><circle cx="12" cy="12" r="2.3"/><circle cx="12" cy="19" r="2.3"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
          <div className="display" style={{ fontSize: 22, lineHeight: 0.95 }}>{g.name}</div>
          <span className="mono" style={{ color: '#fff', fontSize: 11, padding: '4px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)' }}>
            {g.memberCount || members.length || '?'} membres
          </span>
        </div>

        {/* Menu roue dentée */}
        {menuOpen && (
          <>
            <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
            <div style={{ position: 'absolute', top: 46, right: 12, zIndex: 50, background: 'var(--ink-2, #fff)',
              borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.22)', border: '1px solid var(--line)',
              overflow: 'hidden', minWidth: 188 }}>
              {isOwner ? (
                <>
                  <button className="gc-menu-item" onClick={() => { setMenuOpen(false); setRenameValue(g.name); setRenaming(true); }}
                    style={menuItemStyle}>✏️ Renommer le groupe</button>
                  <button className="gc-menu-item" onClick={() => { setMenuOpen(false); setManagingMembers(true); }}
                    style={menuItemStyle}>👥 Gérer les membres</button>
                  <div style={{ height: 1, background: 'var(--line)' }} />
                  <button className="gc-menu-item" onClick={handleDelete} disabled={busy}
                    style={{ ...menuItemStyle, color: 'var(--coral, #D6402E)' }}>🗑️ Supprimer le groupe</button>
                </>
              ) : (
                <button className="gc-menu-item" onClick={handleLeave} disabled={busy}
                  style={{ ...menuItemStyle, color: 'var(--coral, #D6402E)' }}>🚪 Quitter le groupe</button>
              )}
            </div>
          </>
        )}
      </div>

      <div style={{ padding: '10px 12px 6px', borderBottom: '1px solid var(--line)' }}>
        <div style={{ padding: 3, borderRadius: 10, background: 'var(--ink-3)', display: 'flex', gap: 2 }}>
          {GROUP_SORTS.map(s => {
            const on = sortId === s.id;
            return (
              <button key={s.id} onClick={() => setSortId(s.id)} style={{
                flex: 1, padding: '7px 0', borderRadius: 8,
                background: on ? 'var(--sun)' : 'transparent',
                color: on ? 'var(--ink)' : 'var(--text-2)',
                fontSize: 12, fontWeight: 700, border: 'none', cursor: 'pointer',
              }}>{s.label}</button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 8px 10px' }}>
        {sortedMembers.length > 0 ? sortedMembers.map((m, i) => {
          const name = m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || `Membre ${i + 1}`;
          const headline = m[sortCfg.key] ?? '—';
          return (
            <div key={m.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', borderRadius: 10, background: m.isMe ? 'rgba(245,166,35,0.06)' : 'transparent' }}>
              <span className="mono" style={{ width: 22, textAlign: 'center', flexShrink: 0, color: i === 0 ? 'var(--sun-2)' : 'var(--text-3)', fontSize: 12, fontWeight: 700 }}>#{i + 1}</span>
              <div onClick={() => m.id && navigate(`/profil/${m.id}`)}
                style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', background: AVATAR_GRADIENTS[i % 6], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: m.id ? 'pointer' : 'default' }}>
                {m.avatarUrl
                  ? <img src={m.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span className="display" style={{ fontSize: 12, color: '#fff' }}>{memberInitials(name)}</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div onClick={() => m.id && navigate(`/profil/${m.id}`)}
                  style={{ fontSize: 13, fontWeight: 700, cursor: m.id ? 'pointer' : 'default' }}>{name}</div>
              </div>
              <span style={{ fontSize: 16, fontWeight: 700, color: headlineColor, flexShrink: 0 }}>{headline}</span>
            </div>
          );
        }) : (
          <div style={{ padding: '12px 8px', textAlign: 'center' }}>
            <span className="mono" style={{ color: 'var(--text-3)' }}>Aucun membre</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, padding: '6px 14px 14px' }}>
        <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={handleInvite}>+ Inviter</button>
      </div>

      {showInvite && <InviteSheet group={g} onClose={() => setShowInvite(false)} />}

      {/* Renommer */}
      {renaming && (
        <div className="invite-overlay" onClick={() => setRenaming(false)}>
          <form className="card" onClick={e => e.stopPropagation()} onSubmit={handleRename}
            style={{ width: 'min(420px, 92vw)', padding: 20, borderRadius: 18 }}>
            <div className="display" style={{ fontSize: 20, marginBottom: 12 }}>Renommer le groupe</div>
            <input type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)} autoFocus
              style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--line)',
                background: 'var(--ink-2)', color: 'var(--text)', fontSize: 15, marginBottom: 14 }} />
            <div className="row gap-2">
              <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setRenaming(false)}>Annuler</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={busy || !renameValue.trim()}>Enregistrer</button>
            </div>
          </form>
        </div>
      )}

      {/* Gérer les membres (exclusion) */}
      {managingMembers && (
        <div className="invite-overlay" onClick={() => setManagingMembers(false)}>
          <div className="card" onClick={e => e.stopPropagation()}
            style={{ width: 'min(440px, 92vw)', maxHeight: '80vh', overflowY: 'auto', padding: 20, borderRadius: 18 }}>
            <div className="display" style={{ fontSize: 20, marginBottom: 4 }}>Gérer les membres</div>
            <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 14 }}>Exclure un membre le retire du groupe (réversible : il pourra re-rejoindre avec le code).</p>
            <div className="col gap-2">
              {members.map((m, i) => {
                const name = m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || `Membre ${i + 1}`;
                const isThisOwner = m.id === g.owner;
                return (
                  <div key={m.id || i} className="row" style={{ gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', overflow: 'hidden', background: AVATAR_GRADIENTS[i % 6], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {m.avatarUrl
                        ? <img src={m.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span className="display" style={{ fontSize: 12, color: '#fff' }}>{memberInitials(name)}</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{name}{isThisOwner && <span className="chip chip-sun" style={{ marginLeft: 6 }}>Fondateur</span>}</div>
                    </div>
                    {!isThisOwner && (
                      <button className="btn btn-sm" disabled={busy}
                        style={{ background: 'rgba(214,64,46,0.10)', color: 'var(--coral, #D6402E)', flexShrink: 0 }}
                        onClick={() => handleExclude(m.id, name)}>Exclure</button>
                    )}
                  </div>
                );
              })}
            </div>
            <button className="btn btn-ghost" style={{ width: '100%', marginTop: 16 }} onClick={() => setManagingMembers(false)}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}

const menuItemStyle = {
  display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px',
  background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14,
  fontWeight: 600, color: 'var(--text)',
};
